import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Form } from 'src/core/forms';
import { BehaviorSubject, Subject } from 'rxjs';
import { useConstant } from 'src/core/hooks/react-hooks';
import { debounceTime, map, skip, tap, withLatestFrom } from 'rxjs';
import { PriceEstimate, ProductVariant, PriceRange } from 'src/components/ProductDetail/utils/product-detail.types';
import {
    formValuesToCartAddInput,
    formValuesToCartAddSimpleInput,
    formValuesToQty,
} from 'src/components/ProductDetail/utils/selectVariantsFromOptions';
import { CartAddConfigParams, CartAddSimpleParams } from 'src/core/state/cart';
import { AtbDetails } from 'src/components/AtbModal/AtbModal';
import { imagePath } from 'src/util/formatUrl';
import { TierPrice } from 'src/components/ProductDetail/TieredPricing';
import { itemPriceFromTier } from 'src/components/ProductDetail/utils/itemPriceFromTier';
import { ProductSummaryChild } from 'src/components/Basket/utils/basket-customisations';
import { ProductPrice } from 'src/types/global-types';
import { usePrice } from 'src/components/Helpers/Price';
import { VatContext } from 'src/components/AppShell/VatProvider';

export enum Value {
    Idle = 'Idle',
}

export type CartParams =
    | {
          kind: 'config';
          payload: CartAddConfigParams;
      }
    | {
          kind: 'simple';
          payload: CartAddSimpleParams;
      };

export const ProductDetailContext = React.createContext<{
    value: Value;
    qty$: BehaviorSubject<number>;
    itemPrice$: BehaviorSubject<number>;
    atbDetails$: BehaviorSubject<AtbDetails | undefined>;
    submit$: Subject<CartParams>;
    priceEstimates: PriceEstimate[];
}>({
    value: Value.Idle,
    qty$: new BehaviorSubject<number>(0),
    itemPrice$: new BehaviorSubject<number>(0),
    atbDetails$: new BehaviorSubject<AtbDetails | undefined>(undefined),
    submit$: new Subject(),
    priceEstimates: [],
});

export interface InitialProps {
    [index: string]: number | string | any;
}

interface Props {
    price: ProductPrice;
    initialValues: InitialProps;
    initialQty?: number;
    variants: ProductVariant[];

    /**
     * Knowledge about the parent is often needed by
     * other parts of the system, such as ATB
     */
    parentSku: string;
    parentName: string;
    parentImage: string;

    /**
     * These do NOT change per selection, they are set once on the parent
     * so can be used throughout to determine the current item price
     */
    tierPrices: TierPrice[];
    priceEstimates: PriceEstimate[];
    upsellForm?: boolean;

    localVat: null | boolean;
    quantityChanged: (quantity: number) => void;
    price_range?: PriceRange;
}

export const ProductDetailProvider: React.FC<React.PropsWithChildren<Props>> = React.memo((props) => {
    const {
        initialQty,
        variants,
        parentSku,
        parentName,
        parentImage,
        tierPrices,
        priceEstimates,
        localVat,
        quantityChanged,
        price_range,
    } = props;
    const { vat$ } = useContext(VatContext);
    const firstPriceIncl = itemPriceFromTier(tierPrices, 1, true);
    const firstPriceExcl = itemPriceFromTier(tierPrices, 1, false);
    const { amount } = usePrice(firstPriceExcl, firstPriceIncl, vat$.value);
    const qty$ = useConstant(() => new BehaviorSubject<number>(initialQty || 0));
    const submit$ = useConstant(() => new Subject<CartParams>());
    const itemPrice$ = useConstant(() => new BehaviorSubject<number>(Number(amount)));
    const atbDetails$ = useConstant(() => new BehaviorSubject<AtbDetails | undefined>(undefined));
    const value$ = useConstant(() => new BehaviorSubject<any>({}));

    /**
     * Exposed
     */
    const api = useMemo(() => {
        return {
            value: Value.Idle,
            qty$,
            submit$,
            atbDetails$,
            itemPrice$,
            priceEstimates,
        };
    }, [qty$, submit$, atbDetails$, itemPrice$, priceEstimates]);
    /**
     * Change callback for handling the QTY
     */
    const onChange = useCallback(
        (values) => {
            value$.next(values);
            if (variants.length === 0) return;
            const qty = formValuesToQty(values);
            qty$.next(qty);
            itemPrice$.next(itemPriceFromTier(tierPrices, qty, localVat !== null ? localVat : vat$.value));
            quantityChanged(qty);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [itemPrice$, qty$, tierPrices, value$, variants.length, localVat, vat$.value],
    );

    useEffect(() => {
        const sub = vat$
            .pipe(
                withLatestFrom(value$.pipe(map((values) => formValuesToQty(values)))),
                tap(([vat, qty]) => {
                    qty$.next(qty);
                    itemPrice$.next(itemPriceFromTier(tierPrices, qty, localVat !== null ? localVat : vat));
                }),
            )
            .subscribe();
        return () => sub.unsubscribe();
    }, [itemPrice$, qty$, tierPrices, value$, localVat, vat$]);
    /**
     * Submit callback for handling the form submission
     */
    const onSubmit = useCallback(
        (values) => {
            const isConfig = variants.length > 0;
            const qty = formValuesToQty(values);
            const isVat = localVat !== null ? localVat : vat$.value;
            const currentPrice = itemPriceFromTier(tierPrices, qty, isVat);
            const computedPrices =
                currentPrice === 0
                    ? isVat
                        ? Number(price_range?.minimum_price?.final_price_formatted)
                        : Number(price_range?.minimum_price?.final_price_excl_formatted)
                    : currentPrice;

            if (isConfig) {
                const items = formValuesToCartAddInput(parentSku, values, variants);
                const firstImage = imagePath(items.find((x) => x.small_image)?.small_image);
                const without = items.map((item) => {
                    const { attributes, qty, small_image, ...rest } = item;
                    const product_detail_pricing_input = document.querySelector(`input[name='product_detail_pricing']`);

                    let tax_free: boolean | null = false;

                    if (product_detail_pricing_input) {
                        const id = document.querySelector(`input[name='product_detail_pricing']:checked`)?.id;

                        tax_free = id === `product_detail_pricing_1` ? true : false;
                    }

                    return {
                        ...rest,
                        data: { tax_free, ...rest.data },
                    };
                });
                const atbItems: ProductSummaryChild[] = items.map((item) => {
                    return {
                        qty: item.qty,
                        labels: item.attributes.map((attr) => attr.label),
                    };
                });
                const detail: AtbDetails = {
                    image: firstImage,
                    name: parentName,
                    sku: parentSku,
                    qty: qty,
                    itemPrice: computedPrices,
                    items: atbItems,
                };
                const payload: CartParams = { kind: 'config', payload: { items: without } };
                atbDetails$.next(detail);
                submit$.next(payload);
            } else {
                const payload: CartParams = {
                    kind: 'simple',
                    payload: formValuesToCartAddSimpleInput(parentSku, 1) as CartAddSimpleParams,
                };
                const atbItems: ProductSummaryChild[] = [];
                const detail: AtbDetails = {
                    image: parentImage,
                    name: parentName,
                    sku: parentSku,
                    qty: 1,
                    itemPrice: computedPrices,
                    items: atbItems,
                };
                atbDetails$.next(detail);
                submit$.next(payload);
            }
        },
        [
            variants,
            localVat,
            vat$.value,
            tierPrices,
            price_range?.minimum_price?.final_price_formatted,
            price_range?.minimum_price?.final_price_excl_formatted,
            parentSku,
            parentName,
            atbDetails$,
            submit$,
            parentImage,
        ],
    );

    return (
        <Form
            onValueChange={onChange}
            onSubmit={onSubmit}
            initialValues={props.initialValues}
            data-test-id="ProductDetailProvider_form"
        >
            <ProductDetailContext.Provider value={api}>{props.children}</ProductDetailContext.Provider>
        </Form>
    );
});

export function useQty() {
    const { qty$ } = useContext(ProductDetailContext);
    const [qtySelected, setQty] = useState(qty$.value);
    useEffect(() => {
        const sub = qty$.pipe(skip(1), debounceTime(150), tap(setQty)).subscribe();
        return () => sub.unsubscribe();
    }, [qty$]);
    return qtySelected;
}
